import type es from './es';

/** Misma forma que el diccionario base: si falta una clave, TypeScript falla. */
export type DictShape<T> = { [K in keyof T]: T[K] extends string ? string : DictShape<T[K]> };

const en: DictShape<typeof es> = {
  brand: { tagline: 'Learn, practice and compete by typing' },
  nav: {
    home: 'Home', learn: 'Learn', practice: 'Practice', test: 'Speed Test', games: 'Games', compete: 'Compete', stats: 'Stats',
    profile: 'Profile', settings: 'Settings', certificates: 'Certificates', admin: 'Admin', login: 'Sign in', register: 'Sign up',
    logout: 'Sign out', menu: 'Menu', mainNav: 'Main navigation', themeToggle: 'Toggle light/dark theme', skipToContent: 'Skip to content', language: 'Interface language', account: 'Account',
  },
  common: {
    loading: 'Loading…', error: 'Something went wrong', retry: 'Retry', save: 'Save', saved: 'Saved', cancel: 'Cancel', close: 'Close',
    delete: 'Delete', edit: 'Edit', create: 'Create', back: 'Back', next: 'Next', continue: 'Continue', start: 'Start', restart: 'Restart',
    pause: 'Pause', resume: 'Resume', yes: 'Yes', no: 'No', search: 'Search', all: 'All', guest: 'Guest', signIn: 'Sign in',
    signUp: 'Create a free account', seeAll: 'See all', wpm: 'WPM', cpm: 'CPM', accuracy: 'Accuracy', raw: 'Raw', consistency: 'Consistency',
    errors: 'Errors', time: 'Time', words: 'words', chars: 'characters', level: 'Level', xp: 'XP', empty: 'No data yet.', language: 'Language',
    mode: 'Mode', duration: 'Duration', seconds: 's', actions: 'Actions', status: 'Status', copy: 'Copy', copied: 'Copied',
    print: 'Print / PDF', previous: 'Previous', nextPage: 'Next', of: 'of', private: 'Private', public: 'Public',
    signInToUse: 'Sign in to use this feature.', optional: 'optional', date: 'Date', user: 'User', country: 'Country', score: 'Score',
    rank: 'Rank', tests: 'Tests', value: 'Value', none: 'None',
  },
  langs: { es: 'Spanish', en: 'English' },
  layouts: { 'es-qwerty': 'Spanish (QWERTY)', 'en-qwerty': 'English (US QWERTY)' },
  modes: {
    words: 'Words', phrases: 'Phrases', texts: 'Texts', numbers: 'Numbers', punctuation: 'Punctuation', symbols: 'Symbols',
    custom: 'Custom', letters: 'Letters', combos: 'Combinations', weak: 'Weak spots',
  },
  kinds: { test: 'Speed test', practice: 'Practice', lesson: 'Lesson', challenge: 'Challenge', race: 'Race' },
  tiers: { beginner: 'Beginner', basic: 'Basic', intermediate: 'Intermediate', advanced: 'Advanced', expert: 'Expert' },
  categories: {
    intro: 'Introduction', theory: 'Theory', home_row: 'Home row', top_row: 'Top row', bottom_row: 'Bottom row', capitals: 'Capitals',
    numbers: 'Numbers', punctuation: 'Punctuation', symbols: 'Symbols', combined: 'Combined', words: 'Words', phrases: 'Sentences',
    paragraphs: 'Paragraphs', advanced: 'Advanced',
  },
  home: {
    eyebrow: 'Free · No ads · Spanish and English',
    title1: 'Type without looking.',
    title2: 'Type without limits.',
    lead: 'ManiType brings together an academy, guided practice, speed tests, games and competition in one place. Start right now, no sign-up needed.',
    ctaTest: 'Take a speed test',
    ctaLearn: 'Start from scratch',
    tryNow: 'Try it right here: start typing',
    statsTitle: 'The community in real numbers',
    statUsers: 'registered people',
    statTests: 'completed tests',
    statKeystrokes: 'verified keystrokes',
    statToday: 'tests today',
    statsEmpty: 'No tests recorded yet. Be the first person on the leaderboard!',
    featuresTitle: 'Everything you need to type better',
    features: {
      learnTitle: 'Progressive academy',
      learnText: 'From posture and the home row to full paragraphs, across five levels with clear goals.',
      testTitle: 'Honest metrics',
      testText: 'Net and raw WPM, CPM, accuracy and consistency with public formulas verified on the server.',
      smartTitle: 'Smart training',
      smartText: 'Finds your problem keys, combinations and words and suggests tailored exercises.',
      gamesTitle: 'Typing games',
      gamesText: 'Defend your base, beat the clock or survive: practice can be fun too.',
      competeTitle: 'Fair competition',
      competeText: 'Leaderboards per language, daily, weekly and monthly challenges, and real-time races.',
      certTitle: 'Verifiable certificates',
      certText: 'Prove your level with a certificate that has a public verification code.',
    },
    rankingTitle: 'Fastest typists · 60 seconds',
    rankingEmpty: 'The leaderboard is empty. Results appear here once someone completes a ranked test.',
    academyTitle: 'Learn from the very first key',
    academyText: 'Short lessons with an interactive keyboard that shows which finger to use. Progress at your own pace or pick exercises freely.',
    gamesTitle: 'Practice by playing',
    competeTitle: 'Measure yourself against the world',
    competeText: "Take on today's challenge, climb the weekly leaderboards or challenge your friends to a race.",
  },
  typing: {
    clickToFocus: 'Click here or press any key to type',
    paused: 'Paused',
    pausedHint: 'Press Esc or click to resume',
    restartHint: 'Tab · restart',
    pauseHint: 'Esc · pause',
    capsLock: 'Caps Lock is on',
    area: 'Typing area',
    timeLeft: 'Time left',
    progress: 'Progress',
    waiting: 'Start typing to start the timer',
  },
  test: {
    title: 'Speed Test',
    subtitle: 'Measure your real speed and accuracy.',
    time: 'Time',
    wordsCount: 'Words',
    custom: 'Custom',
    customDuration: 'Custom duration (seconds)',
    content: 'Content',
    customText: 'Your text',
    customTextPlaceholder: 'Paste or type the text you want to practice…',
    exerciseLanguage: 'Exercise language',
    rankedBadge: 'Ranked',
    unrankedBadge: 'Not ranked',
    rankedHint: 'Word tests of 15, 30, 60 or 120 s count towards the leaderboards.',
    guestNotice: 'You are a guest: your results are only stored in this browser.',
    loadingText: 'Preparing the text…',
    apply: 'Apply',
  },
  results: {
    title: 'Result',
    official: 'Result verified by the server',
    local: 'Result calculated in your browser',
    net: 'Net WPM',
    raw: 'Raw WPM',
    cpm: 'CPM',
    accuracy: 'Accuracy',
    consistency: 'Consistency',
    errors: 'Errors',
    corrections: 'Corrections',
    uncorrected: 'Uncorrected',
    backspaces: 'Backspaces',
    time: 'Time',
    chars: 'Characters',
    score: 'Score',
    xp: 'XP earned',
    newRecord: 'New personal best!',
    previousRecord: 'Previous best: {wpm} WPM',
    ranked: 'Counts for the leaderboard',
    flagged: 'Result under review: the speed is exceptional and a moderator will verify it.',
    timeline: 'Progress during the test',
    achievements: 'Achievements unlocked',
    levelUp: 'You reached level {level}!',
    streak: 'Streak days: {days}',
    tryAgain: 'Retry',
    next: 'New test',
    saveCta: 'Create an account to keep your history in the cloud, join the leaderboards and earn achievements.',
    formulas: 'How it is calculated',
    formulaText: 'Net WPM = correct characters ÷ 5 ÷ minutes. Raw WPM = all keystrokes ÷ 5 ÷ minutes. Accuracy = correct keystrokes ÷ keystrokes. Corrected errors still count.',
    rejected: 'The server did not accept the result ({reason}).',
    saving: 'Saving result…',
    tooltip: 'Second {s}: {wpm} WPM · raw {raw} · {errors} errors',
  },
  practice: {
    title: 'Practice zone',
    subtitle: 'Train exactly what you need. You can pause whenever you want.',
    options: {
      letters: 'Specific letters', lettersText: 'Choose the letters you want to reinforce.',
      words: 'Words', wordsText: 'Frequent words of the language.',
      phrases: 'Sentences', phrasesText: 'Short sentences with capitals and punctuation.',
      texts: 'Paragraphs', textsText: 'Long texts to build stamina.',
      numbers: 'Numbers', numbersText: 'Figures, decimals and amounts.',
      symbols: 'Symbols', symbolsText: '@ # $ % & ( ) [ ] { } and more.',
      punctuation: 'Punctuation', punctuationText: 'Commas, periods, questions? and exclamations!',
      combos: 'Difficult combinations', combosText: 'Letter groups that are often tricky.',
      weak: 'My weak spots', weakText: 'An exercise generated from the keys you miss most.',
      custom: 'Your own text', customText: 'Practice with any text or word list.',
    },
    lettersLabel: 'Letters to practice',
    lettersPlaceholder: 'e.g. qp;',
    customAsList: 'Treat as a word list (random order)',
    length: 'Length',
    stopOnError: 'Stop on every error',
    weakNone: 'There is not enough data about your errors yet. Take a few tests first.',
    weakUsing: 'Keys to reinforce: {keys}',
    start: 'Start practice',
    change: 'Change exercise',
  },
  learn: {
    title: 'Academy',
    subtitle: 'A progressive path from hand position to expert speed.',
    courseProgress: '{done} of {total} lessons completed',
    continue: 'Continue where I left off',
    lesson: 'Lesson',
    objective: 'Goal',
    instructions: 'Instructions',
    aboutLesson: 'Goal and instructions',
    newKeys: 'New keys',
    targets: 'Target: {wpm} WPM and {acc}% accuracy',
    exercise: 'Exercise {n} of {total}',
    startExercise: 'Start exercise',
    evaluation: 'Evaluation',
    passed: 'Exercise passed!',
    failed: 'You need at least {acc}% accuracy to pass the exercise.',
    recommendation: {
      repeat_accuracy: 'Repeat the exercise calmly: focus on not making mistakes.',
      practice_speed: 'Well done. When you feel comfortable, try going a bit faster to earn the second star.',
      continue: 'Good pace. Move on to the next exercise or improve your accuracy for the third star.',
      excellent: 'Excellent! You have mastered this exercise.',
    },
    lessonCompleted: 'Lesson completed!',
    nextLesson: 'Next lesson',
    nextExercise: 'Next exercise',
    repeat: 'Repeat',
    allLessons: 'All lessons',
    guestProgress: 'Your progress is stored in this browser. Create an account to keep it in the cloud.',
    stars: '{n} of 3 stars',
    keyboardLegend: 'Each colour is one finger',
    fingers: {
      lp: 'Left pinky', lr: 'Left ring', lm: 'Left middle', li: 'Left index', ri: 'Right index', rm: 'Right middle',
      rr: 'Right ring', rp: 'Right pinky', th: 'Thumbs',
    },
  },
  games: {
    title: 'Games',
    subtitle: 'Get better while you play. Guest records are stored in this browser.',
    play: 'Play',
    best: 'Your best: {score}',
    plays: 'Games played: {n}',
    leaderboard: 'High scores',
    score: 'Score',
    lives: 'Lives',
    wave: 'Wave',
    level: 'Level',
    timeLeft: 'Time',
    gameOver: 'Game over',
    newRecord: 'New record!',
    playAgain: 'Play again',
    startHint: 'Press a key or the button to start',
    startHintEnter: 'Choose, then press Enter or the button to start',
    startHintTouch: 'Tap “Play” and type with your device keyboard',
    typeHint: 'Type the highlighted word',
    paused: 'Game paused',
    stats: '{words} words · {wpm} WPM · {acc}% accuracy',
    disabled: 'This game is not available right now.',
    points: 'points',
  },
  icecream: {
    scene: 'Open-air ice cream shop with customers on the pavement',
    sign: 'Ice cream',
    typeHint: 'Type the flavour a customer asks for',
    open: "We're open!",
    howTo: 'Type the flavour each customer asks for to serve their scoop',
    tips: 'Tips',
    streak: 'Streak',
    rating: 'Rating',
    quick: 'quick!',
    starBack: 'Delighted customer!',
    leftWaiting: 'Got tired of waiting',
    levelBanner: 'Level {n}!',
    rush: 'Rush hour: more customers arrive',
    closing: 'The shop closes for today',
    served: 'Customers served: {n}',
    summary: 'Level {level} · best streak: {combo} scoops',
  },
  kites: {
    sky: 'Sky full of word kites',
    typeHint: 'Type the word on a kite',
    howTo: 'Type the word on a kite to catch it before it drifts away',
    waveBanner: 'Wave {n}!',
    faster: 'More kites, and faster',
    combo: 'Combo',
    caught: 'Kites caught: {n}',
    reachedWave: 'You reached wave {n}',
  },
  rally: {
    track: 'Typing Rally track',
    rival1: 'Turbo',
    rival2: 'Sparky',
    rival3: 'Comet',
    place: 'Place',
    rivalsPace: 'Rivals',
    chooseLevel: 'Choose your level',
    levelInfo: 'rivals at {pace} WPM · points ×{mult}',
    distance: 'Race distance',
    distances: { short: 'Short', medium: 'Medium', long: 'Long' },
    tier1: 'Beginner',
    tier2: 'Apprentice',
    tier3: 'Amateur',
    tier4: 'Enthusiast',
    tier5: 'Driver',
    tier6: 'Pro',
    tier7: 'Expert',
    tier8: 'Champion',
    tier9: 'Legend',
    tier10: 'Unstoppable',
    closing: 'Finish closes in',
    getReady: 'Watch the lights: your car only moves with correct letters.',
    finishedPlace: 'Finished {n}',
    notFinished: 'You did not reach the finish in time',
    levelUp: 'Level up to {n}!',
    levelDown: 'Down to level {n} for the next race.',
    levelSame: 'Still on level {n}. Win to level up.',
  },
  compete: {
    title: 'Compete',
    subtitle: 'Per-language leaderboards calculated only with verified results.',
    tabs: { rankings: 'Leaderboards', challenges: 'Challenges', races: 'Races', records: 'Records' },
    type: { speed: 'Speed', accuracy: 'Accuracy', score: 'Score', xp: 'Level' },
    period: { all: 'All time', weekly: 'Weekly', monthly: 'Monthly' },
    accuracyRule: 'Average accuracy with at least {n} tests in the period.',
    scoreRule: 'Sum of scores (WPM × accuracy²) of the tests in the period.',
    you: 'You',
    yourPosition: 'Your position: #{rank}',
    notRanked: 'You are not on this leaderboard yet.',
    empty: 'Nobody has scored on this leaderboard yet. You could be the first!',
    updated: 'Updated {time}',
    challengesIntro: 'Everyone types the same text. Beat the goal to earn extra XP.',
    goal: 'Goal: {wpm} WPM at {acc}%',
    endsIn: 'Ends in {time}',
    participants: 'Participants: {n}',
    accept: 'Accept challenge',
    completed: 'Goal reached',
    yourBest: 'Your best: {score} points ({wpm} WPM)',
    bestScore: 'Your best score: {score}',
    period_daily: 'Daily',
    period_weekly: 'Weekly',
    period_monthly: 'Monthly',
    recordsIntro: 'The best marks of all time by duration.',
    guestChallenges: 'You are playing as a guest: your score is saved in this browser, but it does not enter the challenge leaderboard or earn XP.',
    guestOutcome: 'As a guest, your score is saved only in this browser.',
    joinRanking: 'Create an account to join the leaderboard',
  },
  races: {
    lobby: 'Open rooms',
    create: 'Create room',
    private: 'Private room (code only)',
    joinCode: 'Join with a code',
    join: 'Join',
    noRooms: 'There are no open public rooms. Create one!',
    host: 'Host',
    players: 'Players',
    waiting: 'Waiting for players…',
    shareCode: 'Share this code: {code}',
    ready: "I'm ready",
    notReady: 'Not ready',
    start: 'Start race',
    needPlayers: 'At least 2 players are needed.',
    countdown: 'The race starts in {s}…',
    go: 'Go!',
    finished: 'Race finished',
    position: '#{n}',
    disqualified: 'Disqualified',
    left: 'Left',
    leave: 'Leave room',
    connectionLost: 'Connection to the room was lost.',
    disabled: 'Races are temporarily disabled.',
    history: 'Your latest races',
    you: 'You',
    arrived: '{name} finishes in place {n}',
    guest: 'guest',
    isReady: 'Ready',
    guestName: 'Your nickname',
    guestNameHint: '3 to 20 letters or numbers. You race without an account: your races are not saved and earn no XP.',
    guestDefault: 'Racer',
    guestResult: 'Result validated. Create an account to save your races and earn XP.',
  },
  stats: {
    title: 'Stats',
    subtitle: 'Your progress, calculated from tests you actually completed.',
    guestNotice: 'You are viewing the stats stored in this browser.',
    summary: {
      tests: 'Tests', avgWpm: 'Average WPM', bestWpm: 'Best WPM', avgAccuracy: 'Average accuracy', bestAccuracy: 'Best accuracy',
      words: 'Words typed', keystrokes: 'Keystrokes', practiceTime: 'Practice time', errors: 'Errors', consistency: 'Average consistency',
    },
    evolution: 'Progress',
    granularity: { day: 'Daily', week: 'Weekly', month: 'Monthly', history: 'All time' },
    filters: 'Filters',
    allLanguages: 'All languages',
    allModes: 'All modes',
    allKinds: 'All activities',
    kindFilter: 'Activity type',
    byLanguage: 'By language',
    byMode: 'By mode',
    heatmap: 'Error map by key',
    heatmapHint: 'The more intense the colour, the higher the error rate.',
    recent: 'Latest tests',
    training: 'Smart training',
    goals: 'Your goals: {wpm} WPM and {acc}% accuracy',
    tierNow: 'Estimated current level: {tier}',
    trend: { up: 'Improving (+{d} WPM)', down: 'Dropping ({d} WPM)', flat: 'Stable', insufficient: 'Not enough data for a trend' },
    weakKeys: 'Keys with most errors',
    problemWords: 'Problem words',
    weakBigrams: 'Difficult combinations',
    errorRate: '{rate}% errors',
    doIt: 'Do it now',
    noRecs: 'No recommendations for now: keep practicing!',
    level: 'Level progress',
    xpToNext: '{xp} XP to level {level}',
    achievements: 'Achievements',
    unlockedCount: '{n} of {total} unlocked',
  },
  recs: {
    more_data: 'Tests needed to receive personalised recommendations: {needed}.',
    practice_weak_keys: 'Your weak keys are {keys}. "{worst}" fails {rate}% of the time (your average is {average}%).',
    practice_slow_keys: 'You are slower on {keys} (≈{latency} ms per keystroke). Practice them to gain fluency.',
    practice_bigrams: 'The combinations {bigrams} concentrate errors (up to {rate}%).',
    practice_words: 'Repeat these words you often miss: {words}.',
    focus_accuracy: 'Your average accuracy is {accuracy}%. Slow down a little until you exceed {goal}%.',
    push_speed: 'Your accuracy is excellent. Time to push: target {goal} WPM.',
    advance_level: 'Ready for the {next} level! Last 5 tests: {wpm} WPM and {accuracy}%.',
    review_basics: 'Review the beginner lessons to consolidate your hand position.',
  },
  profile: {
    title: 'Profile',
    joined: 'Member since {date}',
    privateProfile: 'This profile is private.',
    notFound: 'That user does not exist.',
    records: 'Personal records',
    achievements: 'Achievements',
    stats: 'Public stats',
    statsHidden: 'The stats of this profile are hidden.',
    edit: 'Edit profile',
    streak: 'Current streak',
    longestStreak: 'Best streak',
    noAchievements: 'No achievements yet.',
  },
  settings: {
    title: 'Settings',
    sections: { appearance: 'Appearance', typing: 'Typing', language: 'Language and keyboard', profile: 'Profile', privacy: 'Privacy', security: 'Security' },
    theme: 'Theme',
    themeLocked: 'Locked · {requirement}',
    unlockLevel: 'level {level}',
    unlockAchievement: 'achievement "{name}"',
    accent: 'Accent colour',
    accentReset: "Use the theme's colour",
    fontSize: 'Typing text size',
    caret: 'Caret',
    caretStyles: { line: 'Line', block: 'Block', underline: 'Underline' },
    stopOnError: 'Stop on errors (do not advance until correct)',
    showKeyboard: 'Show on-screen keyboard',
    sound: 'Key press sound',
    uiLanguage: 'Interface language',
    exerciseLanguage: 'Exercise language',
    layout: 'Keyboard layout',
    displayName: 'Display name',
    avatar: 'Avatar',
    avatarColor: 'Avatar colour',
    country: 'Country',
    noCountry: 'Not specified',
    bio: 'Bio',
    visibility: 'Profile visibility',
    visibilityHint: 'A private profile only shows your username on leaderboards.',
    showStats: 'Show my stats on my public profile',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordChanged: 'Password updated. Your other sessions were signed out.',
    deleteAccount: 'Delete account',
    deleteWarning: 'Your profile, history, stats, achievements and certificates will be permanently deleted.',
    deleteConfirm: 'Type your password to confirm',
    deleted: 'Your account has been deleted.',
    guestNotice: 'These settings are stored in your browser. Sign in to sync them.',
  },
  themes: { midnight: 'Midnight', paper: 'Paper', ocean: 'Ocean', forest: 'Forest', sand: 'Sand', mono: 'Mono', sunset: 'Sunset', neon: 'Neon', sakura: 'Sakura' },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to save your progress and compete.',
    registerTitle: 'Create your free account',
    registerSubtitle: 'Keep your history, earn achievements and join the leaderboards.',
    identifier: 'Username or email',
    username: 'Username',
    usernameHint: '3–24 characters: letters, numbers and underscore.',
    email: 'Email',
    emailHint: 'You can use it to sign in, just like your username. Never shown publicly.',
    password: 'Password',
    passwordHint: "At least 8 characters. A long phrase is best. Keep it safe: it can't be recovered yet.",
    submitLogin: 'Sign in',
    submitRegister: 'Create account',
    noAccount: "Don't have an account?",
    haveAccount: 'Already have an account?',
    privacy: 'By signing up you agree that we store your results to show you statistics. We do not share your data.',
    welcome: 'Hi, {name}!',
    loggedOut: 'You have signed out.',
    passwordStrength: { weak: 'Weak', fair: 'Fair', strong: 'Strong' },
  },
  certs: {
    title: 'Certificates',
    subtitle: 'Certify your speed with a ranked 60 or 120 second test.',
    disclaimer: 'ManiType certificates attest a result achieved on the platform. They are not an official or academic accreditation.',
    requirements: 'Requirements by category',
    requirementRow: '{category}: {wpm} WPM and {acc}% accuracy',
    eligible: 'Results you can certify',
    noEligible: 'You have no results that meet the requirements yet. Take a 60 or 120 second test.',
    issue: 'Issue certificate',
    mine: 'Your certificates',
    none: 'You have no certificates yet.',
    view: 'View certificate',
    verifyTitle: 'Certificate verification',
    verifyCode: 'Verification code',
    verifyLookup: 'Verify',
    valid: 'Valid certificate',
    revoked: 'Revoked certificate',
    notFound: 'There is no certificate with that code.',
    certTitle: 'Typing certificate',
    certifies: 'ManiType certifies that',
    achieved: 'has reached a speed of',
    withAccuracy: 'with an accuracy of',
    inTest: 'in a {duration} second test in {language}.',
    category: 'Category',
    issuedOn: 'Issued on {date}',
    verifyAt: 'Verifiable at {url}',
    categories: { basic: 'Basic', intermediate: 'Intermediate', advanced: 'Advanced', expert: 'Expert' },
  },
  admin: {
    title: 'Administration',
    tabs: { overview: 'Overview', users: 'Users', content: 'Content', reports: 'Reports', certificates: 'Certificates', audit: 'Audit', settings: 'Settings' },
    overview: {
      users: 'Users', new_users_week: 'New (7 days)', results_today: 'Results today', flagged_results: 'Results under review',
      security_events_day: 'Security events (24 h)', texts: 'Texts', lessons: 'Lessons', certificates: 'Active certificates',
    },
    resources: { courses: 'Courses', lessons: 'Lessons', exercises: 'Exercises', texts: 'Texts', achievements: 'Achievements', challenges: 'Challenges', games: 'Games', languages: 'Languages' },
    role: 'Role',
    roles: { user: 'User', editor: 'Editor', admin: 'Administrator', superadmin: 'Super administrator' },
    suspend: 'Suspend',
    activate: 'Reactivate',
    statuses: { active: 'Active', suspended: 'Suspended' },
    flagged: 'Flagged results',
    security: 'Security events',
    approve: 'Approve',
    reject: 'Reject',
    revoke: 'Revoke',
    revokeReason: 'Revocation reason',
    newItem: 'New item',
    editItem: 'Edit item',
    confirmDelete: 'Delete this item? This cannot be undone.',
    jsonHint: 'JSON format',
    settingsKeys: { registration_open: 'Registration open', races_enabled: 'Races enabled', announcement: 'Home page announcement' },
    noPermission: 'You do not have permission for this section.',
    actor: 'Actor',
    action: 'Action',
    entity: 'Entity',
    details: 'Details',
    lastLogin: 'Last login',
    flags: 'Signals',
    filter: 'Filter',
    saved: 'Changes saved',
  },
  achievements: {
    title: 'Achievements',
    locked: 'Locked',
    unlockedOn: 'Unlocked on {date}',
    reward: 'Reward: {theme} theme',
  },
  errors: {
    network_error: 'Could not connect to the server. Check your connection.',
    invalid_credentials: 'Wrong username or password.',
    username_taken: 'That username is already taken.',
    email_taken: 'That email is already registered.',
    validation_error: 'Please check the highlighted fields.',
    account_suspended: 'This account is suspended.',
    registration_closed: 'Registration is temporarily closed.',
    rate_limited: 'Too many attempts. Please wait a minute.',
    unauthorized: 'You need to sign in.',
    forbidden: 'You are not allowed to do this.',
    insufficient_role: 'You are not allowed to do this.',
    not_found: 'Not found.',
    theme_locked: 'That theme is still locked.',
    result_rejected: 'The result did not pass validation.',
    test_expired: 'The test expired. Start another one.',
    test_already_submitted: 'This test was already submitted.',
    challenge_closed: 'This challenge has ended.',
    certificate_already_issued: 'A certificate already exists for that result.',
    certificate_requirements_not_met: 'That result does not meet the requirements.',
    room_not_found: 'The room does not exist or has expired.',
    room_full: 'The room is full.',
    race_in_progress: 'The race has already started.',
    not_enough_players: 'At least 2 players are needed.',
    races_disabled: 'Races are temporarily disabled.',
    guest_required: 'Your guest spot could not be prepared. Reload the page.',
    invalid_guest_name: 'Choose a nickname of 3 to 20 letters or numbers.',
    too_many_rooms: 'Too many rooms are open. Join one or try again in a moment.',
    cannot_modify_self: 'You cannot modify your own account from here.',
    duplicate_value: 'An item with that value already exists.',
    invalid_reference: 'The reference does not exist.',
    constraint_violation: 'The data does not meet the constraints.',
    no_texts_available: 'There are no texts available for that combination.',
    internal_error: 'Internal error. Please try again.',
    default: 'An unexpected error occurred ({code}).',
  },
  footer: {
    about: 'ManiType is a free platform to learn and improve touch typing.',
    formulas: 'How we measure',
    verify: 'Verify a certificate',
    rights: 'Original content of the ManiType project.',
  },
  notFound: {
    title: 'Page not found',
    text: 'The page you are looking for does not exist or has moved.',
    home: 'Back to home',
  },
};

export default en;
